import { Check, Copy, Home, RotateCw } from 'lucide-react';
import { useMemo, useState } from 'react';

import { HeroPattern } from '@/components/hero-pattern';
import { FlagSyncLogo } from '@/components/svgs/logo';
import { Button } from '@/components/ui/button';
import {
  TypographyH1,
  TypographyH2,
  TypographyLead,
} from '@/components/ui/typography';

interface ErrorScreenProps {
  status: number;
  statusText?: string;
  message?: string;
  routePath?: string;
  /**
   * Override the default reload action. Defaults to `window.location.reload()`.
   */
  onRetry?: () => void;
  /**
   * Override the default home destination. Defaults to `/dashboard`.
   */
  homeHref?: string;
}

export function ErrorScreen({
  status,
  statusText,
  message,
  routePath,
  onRetry,
  homeHref = '/dashboard',
}: ErrorScreenProps) {
  const [copied, setCopied] = useState(false);

  const timestamp = useMemo(
    () =>
      new Date()
        .toISOString()
        .replace('T', ' ')
        .replace(/\..+Z$/, ' UTC'),
    [],
  );

  const path =
    routePath ??
    (typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/');

  const isMissing = status === 404;

  const heading = `${status}`;

  const subheading =
    statusText ||
    (isMissing ? `We can't find ${path}` : 'Something went wrong on our end');

  const description = isMissing ? (
    "The page you're looking for may have moved, or the link that brought you here might be out of date."
  ) : (
    <>
      We&rsquo;re not entirely sure what happened. Try again, or contact{' '}
      <a
        href="mailto:support@flagsync.com"
        className="text-primary underline-offset-4 hover:underline"
      >
        support@flagsync.com
      </a>{' '}
      if the problem persists.
    </>
  );

  // Note: never include `message` or stack details in user-visible diagnostics —
  // they can leak internal paths, env, or business logic. Only safe metadata.
  const copyDiagnostics = async () => {
    const payload = [
      'flagsync diagnostic',
      `status     ${status}`,
      `route      ${path}`,
      `timestamp  ${timestamp}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard unavailable
    }
  };

  const handleRetry = () => {
    if (onRetry) onRetry();
    else if (typeof window !== 'undefined') window.location.reload();
  };

  // `message` is intentionally unused — kept in the prop signature for
  // boundaries to forward, but we never render it. Underlying error details
  // belong in Sentry, not in the user's browser.
  void message;

  return (
    <>
      <HeroPattern />
      <main className="flex min-h-screen flex-col items-center justify-center px-6 py-16 lg:px-8">
        <div className="flex w-full max-w-2xl flex-col items-center gap-8 text-center">
          <FlagSyncLogo className="h-16 w-16" />
          <div className="flex flex-col items-center gap-4">
            <TypographyH1 className="w-fit text-5xl font-extrabold font-mono">
              {heading}
            </TypographyH1>
            <TypographyH2 className="border-0 text-2xl text-muted-foreground">
              {subheading}
            </TypographyH2>
            <TypographyLead className="max-w-lg text-sm">
              {description}
            </TypographyLead>
          </div>

          <div className="w-full max-w-xl rounded-md border border-border bg-card/50 backdrop-blur-sm">
            <DetailRow label="Status" value={String(status)} />
            <DetailRow label="Route" value={path} />
            <DetailRow label="Timestamp" value={timestamp} />
            <div className="border-t border-border px-4 py-2 text-right">
              <button
                type="button"
                onClick={copyDiagnostics}
                className="inline-flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-primary" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy diagnostics
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button onClick={handleRetry}>
              <RotateCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
            <Button asChild variant="outline">
              <a href={homeHref}>
                <Home className="mr-2 h-4 w-4" />
                Back to dashboard
              </a>
            </Button>
          </div>
        </div>
      </main>
    </>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-4 border-b border-border px-4 py-2.5 text-left text-sm last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono text-foreground/90" title={value}>
        {value}
      </dd>
    </div>
  );
}
